import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Observable, filter, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AccountManagerDashboardFollowupType } from 'src/app/accountmanagers/dashboard/enums/account-manager-dashboard.enum';
import {
    AccountManagerDashboardFollowupItem,
    UserFollowupTimeBuckets
} from 'src/app/accountmanagers/dashboard/models/account-manager-dashboard-tile-followups.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { combine } from 'src/app/shared/utilities/route';
import { openConfirmationDialog } from '../actions/confirmation-dialog.actions';
import { showToasterMessage } from '../actions/toaster.actions';
import { ConfirmationDialogSettings } from '../models/confirmation-dialog-settings.model';
import { FacilityNote } from '../models/facility-note.model';
import {
    selectConfirmationDialogData,
    selectConfirmationDialogResult
} from '../selectors/confirmation-dialog.selectors';
dayjs.extend(utc);

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class FacilityFollowupsService {
    facilityFollowupIdToMarkAsComplete: number | null = null;
    facilitySystemFollowupIdToMarkAsComplete: number | null = null;

    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        private readonly _store: Store,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    public markFacilityFollowupComplete(facilityFollowupId: number): Observable<number> {
        const url = `${this.coreApiPath}/facility-system-followups/facility/${facilityFollowupId}/mark-complete`;
        return this.http.put<number>(url, '');
    }

    public markFacilitySystemFollowupComplete(facilitySystemFollowupId: number): Observable<number> {
        const url = `${this.coreApiPath}/facility-system-followups/system/${facilitySystemFollowupId}/mark-complete`;
        return this.http.put<number>(url, '');
    }

    public getAccountManagerDashboardFollowups(
        followupType: AccountManagerDashboardFollowupType,
        utcOffset: number
    ): Observable<AccountManagerDashboardFollowupItem[]> {
        const url = `${this.coreApiPath}/facility-system-followups/account-manager-dashboard-followups?followupType=${followupType}&utcOffset=${utcOffset}`;
        return this.http.get<AccountManagerDashboardFollowupItem[]>(url);
    }

    getUserFollowupTimeBuckets(utcOffset: number): Observable<UserFollowupTimeBuckets> {
        const url = `${this.coreApiPath}/facility-system-followups/user-time-frame-buckets/${utcOffset}`;
        return this.http.get<UserFollowupTimeBuckets>(url);
    }

    appendFollowupDateToNote(facilityNotes: FacilityNote[]): FacilityNote[] {
        return facilityNotes.map((note) => {
            let followupDateLocalString = '';
            if (note.facilityFollowup?.followupDateUtc) {
                followupDateLocalString = `@ ${dayjs.utc(note.facilityFollowup.followupDateUtc).local().format('MM/DD/YYYY hh:mm A')}`;
            } else if (note.facilitySystemFollowup?.followupDateUtc) {
                followupDateLocalString = `@ ${dayjs.utc(note.facilitySystemFollowup.followupDateUtc).local().format('MM/DD/YYYY hh:mm A')}`;
            }
            return { ...note, note: `${note.note} ${followupDateLocalString}` };
        });
    }

    openMarkAsCompleteDialog(facilityFollowupId: number, facilitySystemFollowupId: number): void {
        this.facilityFollowupIdToMarkAsComplete = facilityFollowupId;
        this.facilitySystemFollowupIdToMarkAsComplete = facilitySystemFollowupId;

        if (this.facilityFollowupIdToMarkAsComplete || this.facilitySystemFollowupIdToMarkAsComplete) {
            const settings: ConfirmationDialogSettings = {
                title: 'Mark follow-up as complete?',
                message:
                    'Are you sure you want to mark this follow-up as complete? Completed follow-ups can not be undone and a new follow-up will need to be created if needed.',
                action: 'MARK AS COMPLETE',
                cancelText: 'CANCEL',
                panelClass: ['nova__dialog', 'nova__dialog--md'],
                disableClose: false,
                addtlData: {
                    facilityFollowupIdToMarkAsComplete: this.facilityFollowupIdToMarkAsComplete,
                    facilitySystemFollowupIdToMarkAsComplete: this.facilitySystemFollowupIdToMarkAsComplete
                }
            };

            this._store.dispatch(openConfirmationDialog({ settings: settings }));
        }
    }

    handleMarkAsCompleteConfirmationResult(): Observable<number> {
        return this._store.select(selectConfirmationDialogResult).pipe(
            filter((result) => result),
            withLatestFrom(
                this._store.select(selectConfirmationDialogData) as Observable<{
                    facilityFollowupIdToMarkAsComplete: number;
                    facilitySystemFollowupIdToMarkAsComplete: number;
                }>
            ),
            switchMap(
                ([confirmed, data]: [
                    boolean,
                    {
                        facilityFollowupIdToMarkAsComplete: number;
                        facilitySystemFollowupIdToMarkAsComplete: number;
                    }
                ]) => {
                    if (confirmed) {
                        if (data.facilityFollowupIdToMarkAsComplete) {
                            return this.updateFacilityFollowupStatus(this.facilityFollowupIdToMarkAsComplete ?? -1);
                        } else if (data.facilitySystemFollowupIdToMarkAsComplete) {
                            return this.updateSystemFollowupStatus(this.facilitySystemFollowupIdToMarkAsComplete ?? -1);
                        }
                    }
                    return of(0);
                }
            ),
            untilDestroyed(this)
        );
    }

    updateFacilityFollowupStatus(facilityFollowupId: number): Observable<number> {
        return this.markFacilityFollowupComplete(facilityFollowupId).pipe(
            untilDestroyed(this),
            tap((result) => {
                if (result > 0) {
                    let msg = `${result} facility follow-ups have been completed.`;
                    if (result === 1) {
                        msg = '1 facility follow-up had been completed.';
                    }
                    this._store.dispatch(
                        showToasterMessage({
                            message: msg
                        })
                    );
                }
            })
        );
    }

    updateSystemFollowupStatus(facilitySystemFollowupId: number): Observable<number> {
        return this.markFacilitySystemFollowupComplete(facilitySystemFollowupId).pipe(
            untilDestroyed(this),
            tap((result) => {
                if (result > 0) {
                    let msg = `${result} system follow-ups have been completed.`;
                    if (result === 1) {
                        msg = '1 system follow-up had been completed.';
                    }
                    this._store.dispatch(
                        showToasterMessage({
                            message: msg
                        })
                    );
                }
            })
        );
    }
}
